import React, { useState, useRef } from 'react';
import { Upload, X, AlertCircle, Loader2, FileVideo, Shirt } from 'lucide-react';
import { uploadVideoToGCS, detectJerseys, analyzeTeam } from '../services/api';
import JerseySelector from './JerseySelector';

/**
 * VideoUpload Component - Updated with Jersey Detection Flow
 * 
 * New Flow (Drew's 3:3:1 requirement):
 * 1. Coach uploads video
 * 2. System detects jersey colors (30 seconds)
 * 3. Coach selects which team to analyze
 * 4. Full analysis runs on selected team only
 * 5. Report shows team name (e.g., "Al Nassr") not "Team A"
 */
const VideoUpload = ({ onUploadSuccess }) => {
  // Form state
  const [file, setFile] = useState(null);
  const [opponentTeam, setOpponentTeam] = useState('');
  const [matchDate, setMatchDate] = useState('');
  const [includeTraining, setIncludeTraining] = useState(true);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  // Jersey detection state (NEW)
  const [uploadedVideoInfo, setUploadedVideoInfo] = useState(null);
  const [detectedJerseys, setDetectedJerseys] = useState(null);
  const [showJerseySelector, setShowJerseySelector] = useState(false);
  const [analyzingTeam, setAnalyzingTeam] = useState(false);
  
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB
  const ALLOWED_TYPES = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];

  const validateFile = (file) => {
    if (!file) {
      return 'Please select a video file';
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload MP4, MOV, AVI, or WEBM';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'File size exceeds 2GB limit';
    }
    return null;
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setError(null);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      const validationError = validateFile(droppedFile);
      if (validationError) {
        setError(validationError);
      } else {
        setFile(droppedFile);
      }
    }
  };

  const handleFileChange = (e) => {
    setError(null);
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      const validationError = validateFile(selectedFile);
      if (validationError) {
        setError(validationError);
      } else {
        setFile(selectedFile);
      }
    }
  };

  const removeFile = () => {
    setFile(null);
    setProgress(0);
    setProgressMessage('');
    setError(null);
    setDetectedJerseys(null);
    setShowJerseySelector(false);
    setUploadedVideoInfo(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const resetToUpload = () => {
    removeFile();
    setOpponentTeam('');
    setMatchDate('');
  };

  /**
   * Step 1: Upload video and detect jerseys
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate inputs
    if (!file) {
      setError('Please select a video file');
      return;
    }

    setUploading(true);
    setProgress(0);
    setProgressMessage('Starting upload...');

    try {
      console.log('🎬 Step 1: Uploading video to GCS...');

      // Upload to GCS (get signed URL, upload, return gcsPath)
      const uploadResult = await uploadVideoToGCS(file, (percent, message) => {
        // Only use 0-40% for upload phase
        const adjustedPercent = Math.round(percent * 0.4);
        setProgress(adjustedPercent);
        setProgressMessage(message);
      });

      console.log('✅ Upload complete:', uploadResult);
      
      // Save upload info for later
      setUploadedVideoInfo({
        videoId: uploadResult.videoId,
        gcsPath: uploadResult.gcsPath,
        filename: file.name
      });

      // Step 2: Detect jerseys
      setProgress(45);
      setProgressMessage('Detecting jersey colors...');
      
      console.log('🎽 Step 2: Detecting jerseys...');
      
      const jerseyResult = await detectJerseys(uploadResult.gcsPath, uploadResult.videoId);
      
      console.log('✅ Jersey detection result:', jerseyResult);

      if (!jerseyResult.success) {
        // Jersey detection failed - show error
        throw new Error(jerseyResult.error || 'Could not detect jersey colors. Please upload clearer footage.');
      }

      // Show jersey selector
      setDetectedJerseys(jerseyResult.jerseys);
      setShowJerseySelector(true);
      setProgress(100);
      setProgressMessage('Ready for team selection');

    } catch (err) {
      console.error('❌ Upload/Detection error:', err);
      setError(err.message || 'Upload failed. Please try again.');
      setProgress(0);
      setProgressMessage('');
    } finally {
      setUploading(false);
    }
  };

  /**
   * Step 3: Analyze selected team
   */
  const handleTeamSelect = async (teamId, teamLabel, teamColor) => {
    if (!uploadedVideoInfo) {
      setError('Video information lost. Please upload again.');
      return;
    }

    setAnalyzingTeam(true);
    setError(null);

    try {
      console.log(`🎯 Step 3: Analyzing team ${teamId} (${teamLabel})...`);

      const analysisResult = await analyzeTeam({
        gcsPath: uploadedVideoInfo.gcsPath,
        videoId: uploadedVideoInfo.videoId,
        selectedTeamId: teamId,
        teamLabel: teamLabel || opponentTeam || `Team ${teamId}`,
        teamColor: teamColor
      });

      console.log('✅ Team analysis complete:', analysisResult);

      if (!analysisResult.success) {
        throw new Error(analysisResult.error || 'Analysis failed');
      }

      // Success callback - pass to parent
      if (onUploadSuccess) {
        onUploadSuccess({
          analysisId: analysisResult.video_id,
          opponentTeam: teamLabel || opponentTeam,
          matchDate: matchDate,
          videoUrl: uploadedVideoInfo.gcsPath,
          results: analysisResult.results || analysisResult,
          analyzedTeam: {
            teamId: teamId,
            label: teamLabel,
            color: teamColor
          }
        });
      }

      // Reset form
      resetToUpload();

    } catch (err) {
      console.error('❌ Team analysis error:', err);
      setError(err.message || 'Analysis failed. Please try again.');
    } finally {
      setAnalyzingTeam(false);
    }
  };

  /**
   * Handle cancel from jersey selector
   */
  const handleCancelJerseySelection = () => {
    resetToUpload();
  };

  // Show jersey selector if we have detected jerseys
  if (showJerseySelector && detectedJerseys) {
    return (
      <JerseySelector
        jerseys={detectedJerseys}
        onSelect={handleTeamSelect}
        onCancel={handleCancelJerseySelection}
        loading={analyzingTeam}
        videoInfo={{
          filename: uploadedVideoInfo?.filename,
          gcsPath: uploadedVideoInfo?.gcsPath
        }}
      />
    );
  }

  // Main upload form
  return (
    <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-lg p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Upload Match Video</h2>
        <p className="text-gray-600">
          Upload opponent match footage for AI tactical analysis (supports files up to 2GB)
        </p>
        
        {/* New feature badge */}
        <div className="mt-3 inline-flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm">
          <Shirt className="h-4 w-4" />
          <span><strong>New:</strong> Select which team to analyze by jersey color!</span>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Opponent Team Input */}
        <div>
          <label htmlFor="opponentTeam" className="block text-sm font-medium text-gray-700 mb-2">
            Opponent Team Name (Optional - can set after jersey detection)
          </label>
          <input
            type="text"
            id="opponentTeam"
            value={opponentTeam}
            onChange={(e) => setOpponentTeam(e.target.value)}
            placeholder="e.g., Al Hilal, Al Nassr (or leave blank)"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            disabled={uploading}
          />
          <p className="text-xs text-gray-500 mt-1">
            You can name the team after seeing the jersey colors
          </p>
        </div>

        {/* Match Date Input */}
        <div>
          <label htmlFor="matchDate" className="block text-sm font-medium text-gray-700 mb-2">
            Match Date (Optional)
          </label>
          <input
            type="date"
            id="matchDate"
            value={matchDate}
            onChange={(e) => setMatchDate(e.target.value)}
            max={new Date().toISOString().split('T')[0]}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
            disabled={uploading}
          />
        </div>

        {/* Include Training Checkbox */}
        <div className="flex items-center">
          <input
            type="checkbox"
            id="includeTraining"
            checked={includeTraining}
            onChange={(e) => setIncludeTraining(e.target.checked)}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            disabled={uploading}
          />
          <label htmlFor="includeTraining" className="ml-2 text-sm text-gray-700">
            Include training recommendations in analysis
          </label>
        </div>

        {/* Video Upload Area */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Match Video *
          </label>
          
          {!file ? (
            <div
              className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                dragActive
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                accept="video/mp4,video/quicktime,video/x-msvideo,video/webm"
                className="hidden"
                id="video-upload"
                disabled={uploading}
              />
              
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              
              <label
                htmlFor="video-upload"
                className="cursor-pointer text-blue-600 hover:text-blue-700 font-medium"
              >
                Click to upload
              </label>
              <span className="text-gray-500"> or drag and drop</span>
              
              <p className="text-sm text-gray-500 mt-2">
                MP4, MOV, AVI, or WEBM (up to 2GB)
              </p>
            </div>
          ) : (
            <div className="border border-gray-300 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileVideo className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                {!uploading && (
                  <button
                    type="button"
                    onClick={removeFile}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>

              {/* Progress Bar */}
              {uploading && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
                    <span>{progressMessage || 'Processing...'}</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center space-x-2 text-red-600 bg-red-50 p-4 rounded-lg">
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={uploading || !file}
          className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 ${
            uploading || !file
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>{progressMessage || 'Processing...'}</span>
            </>
          ) : (
            <>
              <Shirt className="h-5 w-5" />
              <span>Upload & Detect Teams</span>
            </>
          )}
        </button>

        <p className="text-xs text-gray-500 text-center">
          After upload, you'll select which team's jersey to analyze. 
          Full analysis typically takes 3-5 minutes.
        </p>
      </form>
    </div>
  );
};

export default VideoUpload;
